import { Smartphone, Mail, Monitor, Wand2 } from "lucide-react";

export const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
export const ALL_DAYS = [0, 1, 2, 3, 4, 5, 6];
export const inputCls = "px-3 py-2 rounded-xl border border-stone-300 text-sm focus:border-amber-600 focus:ring-2 focus:ring-amber-200 outline-none";
const SNOOZE_COUNTS = [0, 1, 2, 3, 4, 5];
const SNOOZE_MINUTES = [2, 5, 10, 15, 20, 30, 45, 60];
const PACES = ["gentle", "medium", "fast"];
const PACE_DIVISORS = { gentle: [1, 1, 1], medium: [1, 2, 4], fast: [1, 3, 5] };
const MIN_SNOOZE_MIN = 2;

export function snoozeLadder(esc) {
  const divs = esc.enabled ? PACE_DIVISORS[esc.pace] : PACE_DIVISORS.gentle;
  return divs.map((d) => Math.max(MIN_SNOOZE_MIN, Math.floor(esc.first_snooze_min / d)));
}

export function Label({ children }) {
  return <label className="text-[10px] font-bold uppercase tracking-widest text-stone-500">{children}</label>;
}

export function HabitAndTime({ f, habits, onChange, onSuggest, suggesting }) {
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      <div>
        <Label>Habit</Label>
        <select data-testid="reminder-habit" value={f.habit_id} onChange={(e) => onChange("habit_id", e.target.value)} className={inputCls + " w-full mt-1 bg-white"}>
          {habits.map((h) => <option key={h.id} value={h.id}>{h.title}</option>)}
        </select>
      </div>
      <div>
        <Label>Time ({f.timezone})</Label>
        <div className="flex gap-1.5 mt-1">
          <input data-testid="reminder-time" type="time" value={f.time} onChange={(e) => onChange("time", e.target.value)} className={inputCls + " flex-1"} />
          <button type="button" data-testid="reminder-suggest-time" onClick={onSuggest} disabled={suggesting} title="Suggest a time from when you usually check in"
            className="inline-flex items-center gap-1 px-3 rounded-xl text-xs font-semibold bg-amber-100 text-amber-800 hover:bg-amber-200 disabled:opacity-50">
            <Wand2 className="w-3.5 h-3.5" /> Suggest
          </button>
        </div>
      </div>
    </div>
  );
}

export function DayPicker({ days, onToggle, onAll }) {
  return (
    <div>
      <Label>Repeat on</Label>
      <div className="flex gap-1.5 mt-1 flex-wrap">
        {DAYS.map((d, i) => (
          <button key={d} type="button" data-testid={`reminder-day-${i}`} onClick={() => onToggle(i)}
            className={`w-11 py-1.5 rounded-full text-xs font-semibold ${days.includes(i) ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600"}`}>{d}</button>
        ))}
        <button type="button" data-testid="reminder-days-all" onClick={onAll} className="px-3 py-1.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">Every day</button>
      </div>
    </div>
  );
}

export function ChannelPicker({ channels, channelSetup, onToggle }) {
  const options = [
    { k: "push", label: "Browser notification", Icon: Smartphone, ok: true },
    { k: "in_app", label: "In-app alarm", Icon: Monitor, ok: true },
    { k: "email", label: "Email", Icon: Mail, ok: !!channelSetup.email },
  ];
  return (
    <div>
      <Label>Reach me by</Label>
      <div className="grid grid-cols-3 gap-2 mt-1">
        {options.map(({ k, label, Icon, ok }) => (
          <button key={k} type="button" data-testid={`reminder-channel-${k}`} disabled={!ok} onClick={() => onToggle(k)}
            title={ok ? "" : "Needs setup — ask your admin to connect this channel"}
            className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold border text-left disabled:opacity-40 ${
              channels[k] && ok ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-white border-stone-200 text-stone-600"}`}>
            <Icon className="w-3.5 h-3.5" /> {label}{!ok && <span className="text-[10px] font-normal">(needs setup)</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

export function EscalationSettings({ esc, onChange }) {
  const ladder = snoozeLadder(esc);
  const stops = esc.max_snoozes < 3 ? ` (stops after ${esc.max_snoozes} snooze${esc.max_snoozes === 1 ? "" : "s"})` : "";
  return (
    <div className="rounded-2xl bg-stone-50 p-4 space-y-3">
      <label className="flex items-center justify-between text-sm">
        <span className="font-semibold text-stone-800">Escalate if I snooze</span>
        <input type="checkbox" data-testid="reminder-escalation" checked={esc.enabled} onChange={(e) => onChange("enabled", e.target.checked)} className="accent-amber-600 w-4 h-4" />
      </label>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Max snoozes</Label>
          <select data-testid="reminder-max-snoozes" value={esc.max_snoozes} onChange={(e) => onChange("max_snoozes", +e.target.value)} className={inputCls + " w-full mt-1 bg-white"}>
            {SNOOZE_COUNTS.map((n) => <option key={n} value={n}>{n === 0 ? "No snoozing" : String(n)}</option>)}
          </select>
        </div>
        <div>
          <Label>First snooze</Label>
          <select data-testid="reminder-first-snooze" value={esc.first_snooze_min} onChange={(e) => onChange("first_snooze_min", +e.target.value)} className={inputCls + " w-full mt-1 bg-white"}>
            {SNOOZE_MINUTES.map((n) => <option key={n} value={n}>{`${n} min`}</option>)}
          </select>
        </div>
      </div>
      {esc.enabled && (
        <div>
          <Label>How fast it ramps up</Label>
          <div className="flex gap-1.5 mt-1">
            {PACES.map((p) => (
              <button key={p} type="button" data-testid={`reminder-pace-${p}`} onClick={() => onChange("pace", p)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold capitalize ${esc.pace === p ? "bg-amber-600 text-white" : "bg-white border border-stone-200 text-stone-600"}`}>{p}</button>
            ))}
          </div>
        </div>
      )}
      <p className="text-xs text-stone-500" data-testid="reminder-preview">
        Preview: chime → snooze {ladder[0]} min → louder → snooze {ladder[1]} min → persistent → snooze {ladder[2]} min{stops}.
      </p>
    </div>
  );
}
